import _ from 'lodash';
import React, {FunctionComponent, useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Grid, makeStyles} from '@material-ui/core';
import {
    ProductAttributesField,
    ProductData,
    ProductDetailsField,
    ProductField,
    ProductInCatalog,
} from '@treez/commons/sharedTypings/product';
import {HandleProductValidationFunction, ProductValidationErrorReport} from '~/lib/errors';
import {cleanProductToEdit, flattenProduct, updateProductState} from '~/lib/product';
import {
    setMergeToProduct,
    updateMergeExactMatches,
    updateMergeProduct,
} from '~/redux/actions/MergeActions';
import {
    selectMergeProduct,
    selectMergeFormDirtyStatus,
    selectProductToMergeToStorePresence,
} from '~/redux/selectors/mergeSelectors';
import BrandTreezColumn from './mergeProductDetails/BrandTreezColumn';
import MergeProductForm from './mergeProductDetails/MergeProductForm';
import MergeTableHeaderCell from './mergeProductDetails/MergeTableHeaderCell';
import MergeProductGrid, {NumberOfColumnsOptions} from './mergeProductGrid/MergeProductGrid';
import StorePresenceCell from './mergeProductGrid/mergeProductColumn/StorePresenceCell';

const {
    AMOUNT,
    BRAND_NAME,
    CLASSIFICATION,
    DESCRIPTIONS,
    ECOMMERCE_NAME,
    NAME,
    OWNER_TYPE,
    SIZE,
    SKU,
    SUBTYPE,
    TYPE,
} = ProductField;

const {EFFECT, FLAVOR, GENERAL, INGREDIENT, INTERNAL} = ProductAttributesField;

const {
    CBD_MG,
    DOSE_CBD_MG,
    DOSE_THC_MG,
    DOSES,
    EXTRACTION_METHOD,
    THC_MG,
    STRAIN,
    GROSS_WEIGHT,
    NET_WEIGHT,
    TOTAL_WEIGHT,
    TOTAL_CONCENTRATE_WEIGHT
} = ProductDetailsField;

interface Props {
    handleProductValidationError: HandleProductValidationFunction;
    hasBeenSubmitted: boolean;
    hideExactMatch: boolean;
    invalidFields: ProductData[];
    products: ProductInCatalog[];
    productValidationErrors: ProductValidationErrorReport;
    removeProduct: (productId: number) => void;
    requiredFields: ProductData[];
    selectExistingProductToMergeTo: (existingProduct: ProductInCatalog) => void;
    setBrandProduct: (brandProduct: ProductInCatalog) => void;
    brandProduct?: ProductInCatalog;
}

const maxNumberOfProductsToShow = 4;

export const MergeProductDetails: FunctionComponent<Props> = (props: Props) => {
    const {
        brandProduct,
        handleProductValidationError,
        hasBeenSubmitted,
        hideExactMatch,
        invalidFields,
        products,
        productValidationErrors,
        removeProduct,
        requiredFields,
        selectExistingProductToMergeTo,
        setBrandProduct,
    } = props;

    const [draftProduct, setDraftProduct] = useState<Partial<ProductInCatalog>>({});
    const [showBrandColumn, setShowBrandColumn] = useState<boolean>(false);
    const [numberOfProductsToShow, setNumberOfProductsToShow] =
        useState<number>(maxNumberOfProductsToShow);
    const [startingProductIndex, setStartingProductIndex] = useState<number>(0);

    const productToMergeTo = useSelector(selectMergeProduct, _.isEqual);
    const dirty = useSelector(selectMergeFormDirtyStatus);
    const productToMergeToStorePresence = useSelector(selectProductToMergeToStorePresence);

    const dispatch = useDispatch();

    const getStartingProduct = () => {
        const equalFields = findEqualFields();

        let startingProduct = {} as Partial<ProductInCatalog>;

        if (_.isEmpty(equalFields) === false) {
            const firstProduct = products[0];

            _.forEach(equalFields, (field) => {
                if (_.includes(ProductDetailsField, field)) {
                    const productDetails = firstProduct.details;
                    const value = _.get(productDetails, field);

                    startingProduct = updateProductState(field, value, startingProduct);
                } else if (_.includes(ProductAttributesField, field)) {
                    const productAttributes = firstProduct.attributes;
                    const value = _.get(productAttributes, field);

                    startingProduct = updateProductState(field, value, startingProduct);
                } else if (field === DESCRIPTIONS) {
                    const productDescriptions = firstProduct.descriptions;
                    const value = _.get(productDescriptions, 'main');

                    startingProduct = updateProductState(field, value, startingProduct);
                } else {
                    const value = _.get(firstProduct, field);

                    startingProduct = updateProductState(field, value, startingProduct);
                }
            });
        }

        return cleanProductToEdit(startingProduct);
    };

    const showNextProduct = () => {
        const lastIndex = products.length - 1;
        const lastShowingProductIndex = startingProductIndex + numberOfProductsToShow - 1;
        if (lastShowingProductIndex < lastIndex) {
            setStartingProductIndex(startingProductIndex + 1);
        }
    };

    const showPreviousProduct = () => {
        if (startingProductIndex > 0) {
            setStartingProductIndex(startingProductIndex - 1);
        }
    };

    useEffect(() => {
        if (showBrandColumn && products.length > maxNumberOfProductsToShow) {
            setNumberOfProductsToShow(3);
        } else if (showBrandColumn === false && products.length > maxNumberOfProductsToShow) {
            setNumberOfProductsToShow(maxNumberOfProductsToShow);
        } else {
            setNumberOfProductsToShow(products.length);
        }
    }, [showBrandColumn, products]);

    const selectableFields = [
        OWNER_TYPE,
        BRAND_NAME,
        NAME,
        ECOMMERCE_NAME,
        TYPE,
        SUBTYPE,
        SIZE,
        AMOUNT,
        CLASSIFICATION,
        ProductField.UOM,
        SKU,
        DOSES,
        CBD_MG,
        THC_MG,
        DOSE_CBD_MG,
        DOSE_THC_MG,
        EXTRACTION_METHOD,
        STRAIN,
        GROSS_WEIGHT,
        NET_WEIGHT,
        TOTAL_WEIGHT,
        TOTAL_CONCENTRATE_WEIGHT,
        DESCRIPTIONS,
        EFFECT,
        FLAVOR,
        INGREDIENT,
        GENERAL,
        INTERNAL,
    ];

    const handleInput = (field: ProductData, value: any) => {
        setDraftProduct(updateProductState(field, value, draftProduct));

        const selectableFields = [
            SUBTYPE,
            TYPE,
            CLASSIFICATION,
            EXTRACTION_METHOD,
            ProductField.UOM,
            DESCRIPTIONS,
        ];

        if (_.includes(ProductAttributesField, field) || _.includes(selectableFields, field)) {
            dispatch(updateMergeProduct(field, value));
        }
    };

    const findEqualFields = (): ProductData[] => {
        const flatProducts = _.map(products, flattenProduct);

        const areValuesEqual = (fieldName: ProductData) => {
            const fields = _.map(flatProducts, fieldName);
            return _.uniqWith(fields, compareValues).length === 1;
        };

        const compareValues = (value1, value2) => {
            if (Array.isArray(value1) && Array.isArray(value2)) {
                return JSON.stringify(value1.sort()) == JSON.stringify(value2.sort());
            }
            return value1 == value2;
        };

        return _.filter(selectableFields, (field) => {
            return areValuesEqual(field);
        }) as ProductData[];
    };

    const getFieldsWithExactMatches = () => {
        const equalFields = findEqualFields();

        return _.filter(selectableFields, (field) => {
            return _.includes(equalFields, field);
        });
    };

    const getFieldsWithTypeConsidered = () => {
        return _.filter(selectableFields, (field) => {
            return !_.includes(invalidFields, field);
        });
    };

    const getFieldsToShow = (): ProductData[] => {
        const fieldsWithTypeConsidered: ProductData[] = getFieldsWithTypeConsidered();

        if (hideExactMatch) {
            const fieldsWithExactMatches: ProductData[] = getFieldsWithExactMatches();

            dispatch(updateMergeExactMatches(fieldsWithExactMatches));

            const fieldsWithTypeConsideredNotMatching: ProductData[] = _.filter(
                fieldsWithTypeConsidered,
                (field) => {
                    return !_.includes(fieldsWithExactMatches, field);
                }
            );

            return fieldsWithTypeConsideredNotMatching;
        }

        return fieldsWithTypeConsidered;
    };

    const setMergeProductToConsensusProductDataValues = () => {
        const productToStartWith = getStartingProduct();
        setDraftProduct(productToStartWith);

        dispatch(setMergeToProduct(productToStartWith));
    };

    const fieldsToShow = getFieldsToShow();

    const brandTreezSearchParams = {
        type: _.map(products, 'type'),
        brandName: _.map(products, 'brandName'),
    } as Record<ProductData, string[]>;

    const useStyles = makeStyles({
        brandContainer: {
            minWidth: '300px',
        },
        formContainer: {
            minWidth: '300px',
        },
        productGridContainer: {
            display: 'flex',
            width: '100%',
        },
    });

    const {brandContainer, formContainer, productGridContainer} = useStyles();

    // Stubbed in for when brand is introduced
    useEffect(() => {
        setShowBrandColumn(false);
    }, [showBrandColumn]);

    useEffect(() => {
        if (dirty === false) {
            setMergeProductToConsensusProductDataValues();
        }
    }, [products, brandProduct]);

    useEffect(() => {
        const localAndGlobalStateEqual = _.isEqual(productToMergeTo, draftProduct);

        if (!localAndGlobalStateEqual) {
            setDraftProduct(productToMergeTo);
        }
    }, [productToMergeTo]);

    return (
        <Grid container justifyContent="center" spacing={0}>
            <Grid item container className={productGridContainer} sm={showBrandColumn ? 7 : 9}>
                <MergeProductGrid
                    fieldsToShow={fieldsToShow}
                    hideExactMatch={hideExactMatch}
                    numberOfColumns={numberOfProductsToShow as NumberOfColumnsOptions}
                    products={products}
                    removeProduct={removeProduct}
                    resetMergeProduct={setMergeProductToConsensusProductDataValues}
                    selectExistingProductToMergeTo={selectExistingProductToMergeTo}
                    startingProductIndex={startingProductIndex}
                    customProductDetailsHeader={
                        <MergeTableHeaderCell
                            numberOfProducts={products.length}
                            numberOfProductsToShow={numberOfProductsToShow}
                            showNextProduct={showNextProduct}
                            showPreviousProduct={showPreviousProduct}
                            startingProductIndex={startingProductIndex}
                        />
                    }
                />
            </Grid>
            {showBrandColumn && (
                <Grid item className={brandContainer} sm={2}>
                    <BrandTreezColumn
                        brandProductToMergeTo={brandProduct}
                        fieldsToShow={fieldsToShow}
                        searchParams={brandTreezSearchParams}
                        setBrandProductToMergeTo={setBrandProduct}
                    />
                </Grid>
            )}
            <Grid item className={formContainer} sm={2}>
                <StorePresenceCell
                    productStorePresence={productToMergeToStorePresence}
                    maxNumberOfStoresToShow={2}
                />
                <MergeProductForm
                    draftProduct={draftProduct}
                    handleInput={handleInput}
                    handleValidationError={handleProductValidationError}
                    hasBeenSubmitted={hasBeenSubmitted}
                    hideExactMatch={hideExactMatch}
                    invalidFields={invalidFields}
                    productValidationErrors={productValidationErrors}
                    requiredFields={requiredFields}
                />
            </Grid>
        </Grid>
    );
};

export default MergeProductDetails;
